import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons/index.js";
import useRestrictedAction from "../../../../hooks/useRestrictedAction.js";
import { resetBeneTransactionsDetails } from "../../../../redux/features/transactions/scheduleBeneficiaryTransactions/index.js";
import { GlobalConfiguration } from "../../../../utils/configurationProvider.js";
import CardContainer from "../../../_utils/CommonViewAllCard/CardContainer/index.jsx";
import TextWithBank from "../../../_utils/CommonViewAllCard/CardSubComponent/TextWithBank/index.jsx";
import TextWithIcon from "../../../_utils/CommonViewAllCard/CardSubComponent/TextWithIcon/index.jsx";

const ScheduleBeneficiaryTransactionViewAllCard = ({
  data,

  currentStatus,
  onClose,
  type,
  filterRef,
  isLabelClicked,
  labelTransactions,
  setLabelTransactions,
  handleLabelSelect,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const getFormattedFrequency = (txn) => {
    const startDate = txn?.transaction_start_date;
    const frequency = txn?.frequency?.toLowerCase();

    if (!frequency) return "";

    if (!startDate && !["daily"].includes(frequency)) return "";

    const dateObj = startDate ? new Date(startDate) : null;

    const day = dateObj?.getDate();
    const weekday = dateObj?.toLocaleString("en-IN", { weekday: "short" });
    const month = dateObj?.toLocaleString("en-IN", { month: "short" });
    const year = dateObj?.getFullYear();

    const formatShortDate = () => `${day} ${month}`;
    const formatFullDate = () => `${day} ${month} ${year}`;

    switch (frequency) {
      case "weekly":
        // Weekly, on Mon
        return `on ${weekday}`;

      case "yearly":
        // Yearly, on 30 Mar
        return `on ${formatShortDate()}`;

      case "monthly":
      case "quarterly":
      case "half_yearly":
      case "fortnightly":
        // Quarterly, from 5 Jan 2025
        return `from ${formatFullDate()}`;

      case "once":
        // Once, on 30 Mar 2025
        return `on ${formatFullDate()}`;

      case "daily":
        // Daily → no date text
        return "";

      default:
        return "";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "var(--pending)";
      case "approved":
        return "var(--awaiting)";
      case "denied":
      case "failed":
      case "rejected":
        return "var(--failed)";
      case "abandoned":
        return "var(--abandoned)";
      case "hold":
      case "queued":
        return "var(--queued)";
      default:
        return "var(--settled)";
    }
  };
  const { showRestrictedAction } = useRestrictedAction();

  const actionRestricted = () => {
    showRestrictedAction();
  };

  return (
    <div className="standard-card-alignment gap-x-gap-sm gap-y-gap-md">
      {data?.map((transactions) => {
        return (
          <CardContainer
            filterRef={filterRef}
            hoverHeight={GlobalConfiguration?.beneScheduleTxnCardHoverHeight}
            key={transactions?.txn_id}
            isSelected={
              Array.isArray(labelTransactions) &&
              labelTransactions?.some(
                (item) => item?.id === transactions?.txn_id,
              )
            }
            onClickHandler={() => {
              if (type === "ScheduleAccBeneAcc") {
                actionRestricted();
                return;
              }

              if (isLabelClicked) {
                handleLabelSelect?.(transactions);
                return;
              }

              onClose?.call();
              dispatch(resetBeneTransactionsDetails());
              navigate(
                `/beneficiary_transaction_dashboard/schedule_view_all/schedule_detail_page/${transactions?.txn_id}`,
                {
                  state: {
                    isViewAll:
                      location.pathname ===
                      "/beneficiary_transaction_dashboard/schedule_view_all"
                        ? true
                        : false,
                  },
                },
              );
            }}
          >
            <card-body>
              <TextWithIcon
                type={"text"}
                text={transactions?.scheduled_name}
                subText={"Schedule name"}
                textColor={"var(--text-primary)"}
                iconComponentArray={[
                  ...(transactions?.confidential_transaction === "Y"
                    ? [
                        {
                          icon: ICONS?.CONFIDENTIAL_TRANSACTION,
                          title: "confidential",
                          isDisabled: false,
                        },
                      ]
                    : []),
                  ...(isLabelClicked
                    ? [
                        {
                          icon:
                            Array.isArray(labelTransactions) &&
                            labelTransactions.some(
                              (item) => item?.id === transactions?.txn_id,
                            )
                              ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                              : ICONS?.SELECT_ALL_WITH_WHITE_OUTLINE_CIRCLE,
                          isDisabled: false,
                        },
                      ]
                    : []),
                ]}
              />

              <TextWithIcon
                type={"Amount"}
                fontWeight={"semibold"}
                text={transactions?.amount}
                subText={"Amount"}
                textColor={"var(--settled)"}
              />

              <TextWithIcon
                type={"text"}
                text={transactions?.beneficiary_account_name}
                subText={"Account name"}
                textColor={"var(--text-primary)"}
              />

              <TextWithBank
                text={(() => {
                  const freq = transactions?.frequency?.toLowerCase();
                  const formattedFreq =
                    freq === "half_yearly"
                      ? "Half yearly"
                      : freq?.charAt(0).toUpperCase() + freq?.slice(1);
                  const formattedDate = getFormattedFrequency(transactions);

                  return formattedDate
                    ? `${formattedFreq}, ${formattedDate}`
                    : formattedFreq;
                })()}
                subText={"Frequency"}
                type={"Text"}
                ifscCode={transactions?.beneficiary_account_ifsc_code}
              />
            </card-body>
            <card-hover>
              <TextWithIcon
                text={
                  transactions?.lifecycle_status === "deleted"
                    ? "Discarded"
                    : currentStatus === "Require approval"
                      ? "Require approval"
                      : transactions?.lifecycle_status
                }
                subText={"Approval status"}
                textCase={"capitalize"}
                fontWeight="semibold"
                textColor={
                  transactions?.lifecycle_status === "deleted"
                    ? "var(--abandoned)"
                    : getStatusColor(transactions?.lifecycle_status)
                }
              />

              <TextWithIcon
                text={transactions?.category_id?.category_name}
                subText={"Category"}
                textCase={"capitalize"}
                textColor={"var(--text-primary)"}
              />

              <TextWithIcon
                text={transactions?.labels?.join(", ") || "No labels assigned"}
                fontWeight={"normal"}
                textColor={"var(--text-primary)"}
                iconComponentArray={[
                  {
                    icon: ICONS?.LABEL,
                  },
                ]}
              />
            </card-hover>
          </CardContainer>
        );
      })}
    </div>
  );
};

export default ScheduleBeneficiaryTransactionViewAllCard;
